import { logger } from "@origin-digital/reporting-client";
import { getOriginCustomerId } from "@origin-digital/auth-utils";
import {
  fetchAccessToken,
  fetchPlatformParameters,
} from "@origin-digital/event-dispatcher";
import { shouldAttemptAuthFetch } from "@origin-digital/tal-client-common";

import { BackendCrm } from "@origin-digital/platform-enums";
import { fetchUserHash } from "./userHash";
import { IIntercomSettings, initIntercom } from "./initialize";
import { LOG_TAG } from "./logTag";

interface IUserInfo {
  userId: string;
  backends: BackendCrm[];
}

// TODO: This check will be moved to the page manifest of tal apps (P21-579)
export function shouldInitialize(): boolean {
  const platformApps = [
    "/auth/callback",
    "/logout",
    "/auth/ml",
    "/auth/sml",
    "/my-account/contexthub",
  ];
  // Brand login and logout pages, e.g. /auth/callback/1e and /logout/1e.
  const brandPlatformAppPrefixes = ["/auth/callback/", "/logout/"];
  const pathName = window.location.pathname;
  const isPlatformApp =
    platformApps.some((app) => app === pathName) ||
    brandPlatformAppPrefixes.some((prefix) => pathName.startsWith(prefix));

  if (isPlatformApp) {
    return false;
  }

  return true;
}

export async function getUserInfo(): Promise<IUserInfo | undefined> {
  if (!shouldAttemptAuthFetch()) {
    // Neither this page's manifest requires login, nor does the visitor
    // already have a session cookie (see shouldAttemptAuthFetch()'s doc
    // comment) — fetchAccessToken's underlying silent Auth0 check has no
    // reason to succeed here. Skip it entirely rather than making a call
    // that's guaranteed to fail.
    return undefined;
  }
  try {
    const result = await fetchAccessToken({});

    if (result && result.accessToken) {
      const userId = getOriginCustomerId(result.accessToken);
      const tokenResponse = await fetchPlatformParameters({});
      const backends = tokenResponse?.backends ?? [];
      return userId ? { userId, backends } : undefined;
    } else {
      logger.debug(`${LOG_TAG} getUserInfo: accessToken is undefined`);
      return undefined;
    }
  } catch (e) {
    // fetchAccessToken ultimately delegates to @od/origin-auth's getJwt(),
    // which marks its rejection `expectedUnauth: true` when the failure is
    // just "visitor isn't logged in" (already logged there at `debug`) —
    // don't re-log that as our own `error` on top of it. Anything else
    // (network failure, misconfigured client, etc.) stays at `error`.
    const logFailure = (e as { expectedUnauth?: boolean })?.expectedUnauth
      ? logger.debug
      : logger.error;
    // Pass `e` so the log actually carries the reason — previously this was
    // logged with no data at all, useless for diagnosing anything beyond
    // "it failed". Safe from reporting-client's separate, unconditional
    // `console.error(data.error)` side-effect (fires whenever the data
    // payload has a truthy `.error` key, regardless of level): the error
    // getJwt()/handleFailedSilentAuth rejects with only ever carries
    // `expectedUnauth` — never an `.error` property — so that side-effect
    // doesn't trigger here.
    logFailure(`${LOG_TAG} getUserInfo failed`, e);
    return undefined;
  }
}

export async function fetchUserHashAndHandleFetchError(): Promise<
  string | undefined
> {
  if (!shouldAttemptAuthFetch()) {
    // Same reasoning as getUserInfo() above: fetchUserHash's
    // authorization: "jwt" request has no reason to succeed here, so skip
    // it entirely rather than making a call that's guaranteed to fail.
    return undefined;
  }
  try {
    const { userHash } = await fetchUserHash("WEB", false);
    return userHash;
  } catch (e) {
    // fetchUserHash's authorization: "jwt" request goes through daxi's
    // getJwt() (see @origin-digital/daxi/src/helpers.ts), which calls the
    // same fetchAccessToken() as getUserInfo() above and — critically —
    // isn't wrapped in its own try/catch, so @od/origin-auth's rejection
    // (marked `expectedUnauth: true` for "visitor isn't logged in", already
    // logged there at `debug`) passes through daxi untouched. Same
    // classification as getUserInfo(): don't re-log the expected case as our
    // own `error`; anything else stays at `error`. See that function's
    // comment for why `e` is passed instead of `...e`.
    const logFailure = (e as { expectedUnauth?: boolean })?.expectedUnauth
      ? logger.debug
      : logger.error;
    logFailure(`${LOG_TAG} fetchUserHash failed`, e);
    return undefined;
  }
}
export async function initWebChat(settings?: IIntercomSettings): Promise<void> {
  const userInfo = await getUserInfo();
  const backends = userInfo ? userInfo.backends : [];

  if (!shouldInitialize()) {
    // Do not initialise for platform specific and kraken apps
    return;
  }

  const userHash = await fetchUserHashAndHandleFetchError();

  // eslint-disable-next-line no-negated-condition
  if (!userInfo) {
    // when userId or userHash is missing, install settings for visitors/leads
    return initIntercom(backends, userHash, settings);
  } else {
    // when userId and userHash are present, install settings for users
    return initIntercom(backends, userHash, {
      ...settings,
      user_id: userInfo.userId,
    });
  }
}
